import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Breadcrumbs } from '../../../Home/Breadcrumb';
import Accordion from '../../../../components/Accordion';
import Confirm from '../../../../components/Confirm';
import FormInput from '../../../../components/Form/FormInput';
import { useInstalacion } from '../../../../hooks/useInstalacion';
import { useAuth } from '../../../../hooks/useAuth';
import { Instalacion } from '../../../../types/Instalacion';

type FormInstalacionErrors = {
  nombre?: string;
  domicilio?: string;
  localidad?: string;
};

export const CrearInstalacion = () => {
  const {
    instalacionInitial,
    instalacionSelected,
    guardarInstalacion,
    buscarInstalacion,
    handleInstalacionSelected,
  } = useInstalacion();
  const { getUsuarioLogueado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const [instalacionForm, setInstalacionForm] = useState<Instalacion>(
    instalacionInitial
  );
  const [formErrors, setFormErrors] = useState<FormInstalacionErrors>(
    {}
  );
  const [edit, setEdit] = useState<boolean>(false);
  const [view, setView] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const toggle = () => setShowModal(!showModal);

  useEffect(() => {
    if (id) {
      buscarInstalacion(id);
      setEdit(true);
      if (searchParams.get('action') === 'view') {
        setView(true);
      } else {
        setView(false);
      }
    } else {
      handleInstalacionSelected(instalacionInitial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchParams]);

  useEffect(() => {
    if (id) {
      setInstalacionForm(instalacionSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instalacionSelected]);

  const onChange = (e: any) => {
    const { name, value } = e.target;
    setInstalacionForm({
      ...instalacionForm,
      [name]: value,
    });
  };

  const validateForm = (instalacion: Instalacion) => {
    const errors: FormInstalacionErrors = {};
    if (!instalacion.nombre) {
      errors.nombre = 'El campo Nombre es obligatorio';
    }
    if (!instalacion.domicilio) {
      errors.domicilio = 'El campo Domicilio es obligatorio';
    }
    if (!instalacion.localidad) {
      errors.localidad = 'El campo Localidad es obligatorio';
    }
    setFormErrors(errors);
    return errors;
  };

  const handleGuardar = () => {
    const errores = validateForm(instalacionForm);
    if (Object.keys(errores).length > 0) {
      return;
    }

    // La Empresa Gestora no es editable: siempre se completa con la
    // del usuario Empresa Gestora logueado.
    const idEmpresaGestora = getUsuarioLogueado()?.idEmpresaGestora;

    guardarInstalacion({
      ...instalacionForm,
      idEmpresaGestora,
    })
      .then(() => {
        toast.success('Se guardó la Instalación correctamente');
        navigate('/consultaInstalaciones');
      })
      .catch(() =>
        toast.error('Hubo un error al guardar la Instalación')
      );
  };

  const handleCancelar = (e: any) => {
    e.preventDefault();
    setShowModal(true);
  };

  const cancelar = () => {
    toggle();
    navigate('/consultaInstalaciones');
  };

  const handleVolver = () => {
    navigate('/consultaInstalaciones');
  };

  return (
    <>
      <ToastContainer />
      <div className="content-page">
        <div className="content">
          <div className="container-fluid">
            <Breadcrumbs
              title={edit ? (view ? 'Vista' : 'Editar') : 'Nueva'}
              titlePage={
                view ? 'Vista Instalación' : 'Instalación'
              }
              nameFirst={'Instalaciones'}
              urlFirst={'/consultaInstalaciones'}
            />
            <Confirm
              isOpen={showModal}
              toggle={toggle}
              title="Cancelar"
              yesFn={cancelar}
            >
              <div className="form-horizontal">
                <div className="row">
                  <p>¿Desea salir sin guardar la Instalación?</p>
                </div>
              </div>
            </Confirm>
            <Accordion id={'instalacion'} title="Instalación">
              <div className="row mt-4">
                <FormInput
                  id={'nombre'}
                  label={'Nombre'}
                  name={'nombre'}
                  value={instalacionForm.nombre || ''}
                  isDisabled={view}
                  onChange={onChange}
                  errorText={formErrors.nombre}
                  maxLength={140}
                />
              </div>
              <div className="row mt-2">
                <FormInput
                  id={'domicilio'}
                  label={'Domicilio'}
                  name={'domicilio'}
                  value={instalacionForm.domicilio || ''}
                  isDisabled={view}
                  onChange={onChange}
                  errorText={formErrors.domicilio}
                  maxLength={140}
                />
              </div>
              <div className="row mt-2">
                <FormInput
                  id={'localidad'}
                  label={'Localidad'}
                  name={'localidad'}
                  value={instalacionForm.localidad || ''}
                  isDisabled={view}
                  onChange={onChange}
                  errorText={formErrors.localidad}
                  maxLength={140}
                />
                <FormInput
                  id={'telefono'}
                  label={'Teléfono'}
                  name={'telefono'}
                  value={instalacionForm.telefono || ''}
                  isDisabled={view}
                  onChange={onChange}
                  type="tel"
                  maxLength={24}
                />
              </div>
            </Accordion>

            <div className="text-center">
              {!view && (
                <>
                  <button
                    className="btn btn-secondary mx-2"
                    onClick={handleCancelar}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-success mx-2"
                    onClick={handleGuardar}
                  >
                    Guardar
                  </button>
                </>
              )}
              {view && (
                <button
                  className="btn btn-secondary mx-2"
                  onClick={handleVolver}
                >
                  Volver
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
